import { useCreateDemoMutation } from '@carboncopyco/data';
import { CloseOutlineIcon, EditIcon } from '@carboncopyco/icons';
import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { Loader as AppLoader, notify } from '@carboncopyco/ui';
import { MemoryRouter, Route, Routes } from 'react-router-dom';
import { useEditorPageInfo } from '../components/EditorContainer/hooks';
import InnerTabsContainer from '../components/InnerTabs/InnerTabsContainer';
import InnerViewArea from '../components/InnerViewArea';
import PresentAndPreviewToolbar from '../components/PresentAndPreviewToolbar';
import { useTabManager } from '../services/TabManager';
import { isActiveOrAllInnerTabsLoaded } from '../shared/utils';
import { useUser } from '../store/context';
import { ResponsiveModeContextProvider } from '../store/context/ResponsiveModeContext';
import { previewFrameBorder } from './constants/previewFrameBorder';
import { useOpenDemoEditorTab } from './utils/openDemoEditorTab';

const PreviewCloneRoute = ({ tab }: { tab: IGlobalTab }) => {
  const { activeOrganization } = useUser();
  const [createDemo, { isLoading: isCreatingDemo }] = useCreateDemoMutation();
  const openDemoEditorTab = useOpenDemoEditorTab();
  const { closeGlobalActiveTab } = useTabManager();
  const pageInfo = useEditorPageInfo(tab);

  const { clone } = tab.payload;
  const isArchived = clone.isDeleted;

  const onExitPreviewClick = () => {
    closeGlobalActiveTab();
  };

  const onStartEditingClick = () => {
    if (!clone) {
      return;
    }

    const folderId = String(clone.folderId);

    createDemo({
      organizationId: activeOrganization?.id.toString(),
      recordingId: Number(clone.id),
      demoName: `Demo for ${clone.recordName}`,
      ...(folderId && folderId !== '0' ? { folderId } : {}),
    })
      .unwrap()
      .then((demo) => {
        closeGlobalActiveTab();
        return openDemoEditorTab(demo, Number(activeOrganization.id));
      })
      .catch(() => notify.error('Error creating demo'));
  };

  return (
    <ResponsiveModeContextProvider>
      <div className="flex flex-col flex-grow flex-shrink-0 basis-auto">
        <PresentAndPreviewToolbar
          tab={tab}
          speakerPannel={{ isVisible: false }}
          ctaButton={
            isArchived
              ? {
                  title: 'Exit preview',
                  onClick: onExitPreviewClick,
                  icon: <CloseOutlineIcon />,
                }
              : {
                  title: 'Start editing',
                  onClick: onStartEditingClick,
                  icon: <EditIcon />,
                  idDisabled: isCreatingDemo,
                }
          }
          pageInfo={pageInfo}
        />
        <InnerTabsContainer globalTab={tab} />
        <div className={`flex grow relative ${previewFrameBorder}`}>
          <InnerViewArea tab={tab} />
          {!isActiveOrAllInnerTabsLoaded(tab) && <AppLoader show />}
        </div>
      </div>
    </ResponsiveModeContextProvider>
  );
};

export const PreviewCloneScreen = ({ tab }: { tab: IGlobalTab }) => (
  <MemoryRouter>
    <Routes>
      <Route
        path="/*"
        element={<PreviewCloneRoute tab={tab} />}
      />
    </Routes>
  </MemoryRouter>
);
